import {
    getEndOfDayOfDateRangeEnd,
    isDateRange,
    type DateRange,
} from "@@/shared/date-time/date-range";
import { timeSlotByDurationFactory } from "@@/shared/date-time/time-slot";
import { type NearestMinutes } from "@@/shared/nearest-minutes";
import {
    getTimeOfDay,
    getTimeOfDayHour,
    getTimeOfDayHourMinute,
    getTimeOfDayRounded,
} from "@@/utilities/date-and-time/time-of-day";
import {
    addDays,
    addSeconds,
    differenceInMilliseconds,
    isBefore,
    roundToNearestMinutes,
} from "date-fns";
import { z } from "zod";
import { repeatIndex } from "../../utilities/repeat";
import { asDate } from "./as-date";
import { toFullDate, type FullDate } from "./full-date";
import { isDate } from "./is-date";
import {
    isoAndUnixFactory,
    isoAndUnixZodSchema,
    type IsoAndUnixTimestamp,
} from "./iso-and-unix";
import {
    convertTimeByGranularity,
    timeGranularity,
    type DurationWithGranularity,
} from "./time-granularity";
import {
    oneDay,
    type DurationInMinutes,
    type DurationInMs,
    type DurationInSeconds,
} from "./time-spans";

type TimeRangeId = string;
type TimeRange = {
    readonly _id: TimeRangeId; // "start.unix-end.unix"
    readonly _type: "TIME_RANGE";
    readonly start: IsoAndUnixTimestamp;
    readonly end: IsoAndUnixTimestamp;
    readonly duration: DurationInSeconds;
};

const timeRangeZodSchema: z.ZodSchema<TimeRange> = z.object({
    _id: z.string(),
    _type: z.literal("TIME_RANGE"),
    start: isoAndUnixZodSchema,
    end: isoAndUnixZodSchema,
    duration: z.number().min(0) as unknown as z.ZodSchema<DurationInSeconds>,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTimeRange = (item: any): item is TimeRange =>
    typeof item !== "undefined" && item._type === "TIME_RANGE";

const timeRangeToString = (
    range: TimeRange,
    options?: {
        onlyStartTime?: boolean;
    },
) => {
    const { start, end, duration } = range;
    const onlyStartTime = !!options?.onlyStartTime;
    return !onlyStartTime && duration
        ? `${getTimeOfDay(start)}-${getTimeOfDay(end)}`
        : getTimeOfDay(start);
};

const timeRangeToStringRounded = (
    { start, end, duration }: TimeRange,
    roundToNearestMinute?: NearestMinutes,
    shortenMinutes = false,
) => {
    if (duration) {
        let canShorten = false;

        const s = roundToNearestMinutes(asDate(start), {
            nearestTo: roundToNearestMinute,
        });
        const e = roundToNearestMinutes(asDate(end), {
            nearestTo: roundToNearestMinute,
        });

        canShorten =
            shortenMinutes && s.getMinutes() === 0 && e.getMinutes() === 0;

        const timeFunc = canShorten ? getTimeOfDayHour : getTimeOfDayHourMinute;

        return `${timeFunc(s)}-${timeFunc(e)}`;
    }
    return getTimeOfDayRounded(start, roundToNearestMinute, shortenMinutes);
};

function timeRangeByDurationFactory(
    start: Date,
    duration: DurationInMs,
): TimeRange;
function timeRangeByDurationFactory(
    start: IsoAndUnixTimestamp,
    duration: DurationInMs,
): TimeRange;
function timeRangeByDurationFactory(
    start: IsoAndUnixTimestamp,
    duration: DurationWithGranularity,
): TimeRange;
function timeRangeByDurationFactory(
    start: Date,
    duration: DurationWithGranularity,
): TimeRange;
function timeRangeByDurationFactory(
    start: Date | IsoAndUnixTimestamp,
    duration: DurationInMs | DurationWithGranularity,
): TimeRange {
    const startDate = isDate(start) ? start : asDate(start);
    const durationInMs =
        typeof duration === "number"
            ? duration
            : convertTimeByGranularity(
                  duration.value,
                  duration.granularity,
                  timeGranularity.MILLISECOND,
              );

    const end = new Date(startDate.getTime() + durationInMs);

    return {
        _id: `${startDate.getTime()}-${end.getTime()}`,
        _type: "TIME_RANGE",
        start: isoAndUnixFactory(startDate),
        end: isoAndUnixFactory(end),
        duration: convertTimeByGranularity(
            durationInMs,
            timeGranularity.MILLISECOND,
            timeGranularity.SECOND,
        ),
    };
}

function timeRangeFactory(dateRange: DateRange): TimeRange;
function timeRangeFactory(start: Date, end: Date): TimeRange;
function timeRangeFactory(
    start: IsoAndUnixTimestamp,
    end: IsoAndUnixTimestamp,
): TimeRange;
function timeRangeFactory(start: IsoAndUnixTimestamp, end?: never): TimeRange;
function timeRangeFactory(
    start: Date | IsoAndUnixTimestamp | DateRange,
    end?: Date | IsoAndUnixTimestamp,
): TimeRange {
    const _start = isDateRange(start) ? asDate(start.start) : asDate(start);
    const _end = (() => {
        if (isDateRange(start)) {
            // turn date range end into a date and time instead of just a date
            // set it to the end of day of the date ranges time zone
            return getEndOfDayOfDateRangeEnd(start);
        }
        return asDate(end);
    })();
    if (!_end) {
        throw new Error("An end parameter for the time range is required");
    }
    const diffInMs = differenceInMilliseconds(_end, _start) as DurationInMs;
    return timeRangeByDurationFactory(_start, diffInMs);
}

/**
 * @deprecated use `timeRangeFactory` instead
 *
 * @param {IsoAndUnixTimestamp} start
 * @param {IsoAndUnixTimestamp} end
 * @return {*}  {TimeRange}
 */
const timeRangeFactoryByIsoAndUnixTimestamp = (
    start: IsoAndUnixTimestamp,
    end: IsoAndUnixTimestamp,
): TimeRange => timeRangeFactory(start, end);

const largerThen = (input1: number, input2: number, strict = true) => {
    return strict ? input1 >= input2 : input1 > input2;
};

function isWithinTimeRange(
    input: TimeRange | IsoAndUnixTimestamp,
    timeRange: TimeRange,
    options?: {
        /**
         * When true compares using `x or equal`, instead of only `x`. \
         * E.g. `>=` instead of `>`
         * defaults to `true`
         * @type {boolean}
         */
        strict?: boolean;
    },
): boolean {
    const strict = options?.strict ?? true;
    const start = isTimeRange(input) ? input.start : input;

    if (
        largerThen(start.unix, timeRange.start.unix, strict) &&
        largerThen(timeRange.end.unix, start.unix, strict)
    ) {
        return true;
    }

    return (
        isTimeRange(input) &&
        largerThen(input.end.unix, timeRange.start.unix, strict) &&
        largerThen(timeRange.end.unix, input.end.unix, strict)
    );
}
function isPartOfTimeRange(
    input: TimeRange | IsoAndUnixTimestamp,
    timeRange: TimeRange,
    options?: {
        /**
         * defaults to `true`
         * @type {boolean}
         */
        strict?: boolean;
    },
): boolean {
    const strict = options?.strict ?? true;

    if (isTimeRange(input)) {
        const start = input.start;
        const end = input.end;

        if (
            largerThen(start.unix, timeRange.start.unix, strict) &&
            largerThen(timeRange.end.unix, start.unix, strict)
        ) {
            return true;
        }
        if (
            largerThen(end.unix, timeRange.start.unix, strict) &&
            largerThen(timeRange.end.unix, end.unix, strict)
        ) {
            return true;
        }

        return false;
    }

    if (
        largerThen(input.unix, timeRange.start.unix, strict) &&
        largerThen(timeRange.end.unix, input.unix, strict)
    ) {
        return true;
    }
    return false;
}
const parseTimeRange =
    <T extends Date | FullDate | IsoAndUnixTimestamp>(
        output: (date: Date) => T,
    ) =>
    (timeRange: TimeRange | undefined): T[] => {
        if (typeof timeRange === "undefined") return [];
        const numberOfDays = timeRange.duration / oneDay;
        return repeatIndex(numberOfDays).map(count => {
            const date = addDays(asDate(timeRange.start), count);
            return output(date);
        });
    };

const addMsTimeToTimeRange = (
    time: TimeRange,
    duration: DurationInMs,
): TimeRange => {
    if (duration === 0) return time;
    const start = asDate(time.start);
    const end = asDate(time.end);
    const newEnd = new Date(end.getTime() + duration);
    return timeRangeFactory(start, newEnd);
};

const addMinutesTimeToTimeRange = (params: {
    timeRange: TimeRange;
    durationBefore: DurationInMinutes;
    durationAfter: DurationInMinutes;
}) => {
    const { durationAfter, durationBefore, timeRange } = params;

    const preparationTimeInSeconds = convertTimeByGranularity(
        durationBefore,
        "MINUTE",
        "SECOND",
    );
    const totalDuration =
        convertTimeByGranularity(timeRange.duration, "SECOND", "MILLISECOND") +
        convertTimeByGranularity(
            durationAfter + durationBefore,
            "MINUTE",
            "MILLISECOND",
        );

    const adjusted = timeSlotByDurationFactory(
        addSeconds(asDate(timeRange.start), -preparationTimeInSeconds),

        totalDuration,
    );

    return adjusted;
};

const isTimeRangeOverDST = (timeRange: TimeRange) => {
    const start = asDate(timeRange.start);
    const end = asDate(timeRange.end);
    return start.getTimezoneOffset() !== end.getTimezoneOffset();
};

const parseDates = parseTimeRange(isoAndUnixFactory);
const parseFullDates = parseTimeRange(toFullDate);

/**
 * Transform an array of time ranges to a single time range \
 * NOTE! needs to contain at least one time range
 */
const mergeTimeRanges = (timeRanges: TimeRange[]): TimeRange => {
    if (!timeRanges.length) {
        // There must be at least one
        throw new Error("Cannot merge empty array of time ranges");
    }
    if (timeRanges.length === 1) {
        // "There can be only one"
        return timeRanges[0];
    }
    const start = timeRanges.reduce((previous, current) => {
        return isBefore(asDate(current.start), asDate(previous.start))
            ? current
            : previous;
    }).start;
    const end = timeRanges.reduce((previous, current) => {
        return isBefore(asDate(previous.end), asDate(current.end))
            ? current
            : previous;
    }).end;
    return timeRangeFactory(start, end);
};

const timeRangeUtils = {
    mergeTimeRanges,
};

export {
    addMinutesTimeToTimeRange,
    addMsTimeToTimeRange,
    isPartOfTimeRange,
    isTimeRange,
    isTimeRangeOverDST,
    isWithinTimeRange,
    parseDates,
    parseFullDates,
    timeRangeByDurationFactory,
    timeRangeFactory,
    timeRangeFactoryByIsoAndUnixTimestamp,
    timeRangeToString,
    timeRangeToStringRounded,
    timeRangeUtils,
    timeRangeZodSchema,
};
export type { DurationWithGranularity as DurationAndGranularity, TimeRange };
